import { useState } from 'react';
import { HelpCircle, Send, CheckCircle, ExternalLink, MessageSquare, Zap } from 'lucide-react';

export default function SupportPage() {
  const [submitted, setSubmitted] = useState(false);

  return (
    <div className="max-w-2xl space-y-6">
      <div>
        <h2 className="text-lg font-semibold text-gray-900">Help & Support</h2>
        <p className="text-gray-500 text-sm mt-1">Get help with your account, report bugs, or request setup assistance</p>
      </div>

      {/* Contact Info */}
      <div className="card p-5">
        <div className="flex flex-wrap gap-6">
          <a href="tel:3059098647" className="flex items-center gap-2 text-primary-600 font-semibold hover:underline">
            <span className="w-8 h-8 bg-primary-100 rounded-lg flex items-center justify-center text-sm">📞</span>
            (305) 909-8647
          </a>
          <a href="mailto:m@y12.ai" className="flex items-center gap-2 text-primary-600 font-semibold hover:underline">
            <span className="w-8 h-8 bg-primary-100 rounded-lg flex items-center justify-center text-sm">✉️</span>
            m@y12.ai
          </a>
        </div>
      </div>

      {/* Quick Help */}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <div className="card p-5">
          <div className="flex items-center gap-3 mb-3">
            <div className="w-9 h-9 bg-blue-100 rounded-lg flex items-center justify-center">
              <Zap className="w-5 h-5 text-blue-600" />
            </div>
            <h3 className="font-medium text-gray-900">Getting Started</h3>
          </div>
          <p className="text-sm text-gray-500 mb-3">
            New here? Go to <strong>Settings</strong> to see how to connect Indeed and LinkedIn to your forms in 3 easy steps.
          </p>
          <a href="/settings" className="text-sm text-primary-600 font-medium hover:underline">
            Go to Settings →
          </a>
        </div>

        <div className="card p-5">
          <div className="flex items-center gap-3 mb-3">
            <div className="w-9 h-9 bg-purple-100 rounded-lg flex items-center justify-center">
              <MessageSquare className="w-5 h-5 text-purple-600" />
            </div>
            <h3 className="font-medium text-gray-900">Advanced Setup</h3>
          </div>
          <p className="text-sm text-gray-500 mb-3">
            Need help with webhooks, Indeed auto-import, or API integrations? Describe your setup below and we'll help.
          </p>
          <a href="#contact-form" className="text-sm text-primary-600 font-medium hover:underline">
            Contact us below →
          </a>
        </div>
      </div>

      {/* Contact Form */}
      <div id="contact-form" className="card overflow-hidden">
        <div className="bg-gradient-to-r from-gray-800 to-gray-900 px-6 py-4">
          <div className="flex items-center gap-3">
            <HelpCircle className="w-5 h-5 text-white" />
            <h3 className="font-semibold text-white">Contact Support</h3>
          </div>
          <p className="text-gray-400 text-sm mt-1">Bug reports, feature requests, or setup assistance</p>
        </div>

        {submitted ? (
          <div className="p-12 text-center">
            <CheckCircle className="w-12 h-12 text-green-500 mx-auto mb-4" />
            <h3 className="text-lg font-semibold text-gray-900 mb-2">Message sent!</h3>
            <p className="text-gray-500 mb-4">We'll get back to you as soon as possible.</p>
            <button
              onClick={() => setSubmitted(false)}
              className="text-sm text-primary-600 font-medium hover:underline"
            >
              Send another message
            </button>
          </div>
        ) : (
          <form
            action="https://formspree.io/f/mbdpabny"
            method="POST"
            encType="multipart/form-data"
            onSubmit={() => {
              setTimeout(() => setSubmitted(true), 500);
            }}
            className="p-6 space-y-5"
          >
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1.5">
                Your email
              </label>
              <input
                type="email"
                name="email"
                required
                className="input"
                placeholder="you@company.com"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1.5">
                What do you need help with?
              </label>
              <select name="category" className="input" required>
                <option value="">Select a category...</option>
                <option value="bug">Bug Report</option>
                <option value="setup">Setup Assistance (Webhooks, Indeed, LinkedIn)</option>
                <option value="feature">Feature Request</option>
                <option value="billing">Billing Question</option>
                <option value="other">Other</option>
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1.5">
                Message
              </label>
              <textarea
                name="message"
                required
                rows={5}
                className="input resize-none"
                placeholder="Describe your issue or question in detail..."
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1.5">
                Screenshot or file <span className="text-gray-400 font-normal">(optional)</span>
              </label>
              <input
                type="file"
                name="upload"
                className="w-full text-sm text-gray-500 file:mr-4 file:py-2 file:px-4 file:rounded-xl file:border-0 file:text-sm file:font-medium file:bg-primary-50 file:text-primary-700 hover:file:bg-primary-100 transition-colors"
              />
            </div>

            <button
              type="submit"
              className="btn-primary w-full py-3 inline-flex items-center justify-center gap-2"
            >
              <Send className="w-4 h-4" />
              Send Message
            </button>
          </form>
        )}
      </div>
    </div>
  );
}
